import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Order, OrderStatus } from '@runninglime/shared';
import { apiGet } from '../../lib/api.ts';
import { formatPrice, formatDate } from '../../lib/utils.ts';
import { StatusBadge, LoadingSpinner } from '../../components/ui/Shared.tsx';

const STEPS: OrderStatus[] = ['pending', 'confirmed', 'packed', 'shipped', 'delivered'];

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const res = await apiGet<Order>(`/orders/${id}`);
      if (res.success && res.data) {
        setOrder(res.data);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return <LoadingSpinner />;
  if (!order) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-dark">Order not found</h2>
        <button onClick={() => navigate('/orders')} className="mt-4 text-green font-medium hover:underline">
          Back to Orders
        </button>
      </div>
    );
  }

  const currentStepIdx = order.status === 'problem'
    ? -1
    : STEPS.indexOf(order.status);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
        <button onClick={() => navigate('/orders')} className="hover:text-green transition-colors">Orders</button>
        <span>/</span>
        <span className="text-dark font-medium">#{order.id.slice(0, 8)}</span>
      </nav>

      {/* Header */}
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-dark mb-1">Order #{order.id.slice(0, 8)}</h1>
          <p className="text-gray-500">Placed on {formatDate(order.createdAt)}</p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {/* Status Timeline */}
      <div className="glass rounded-2xl p-6 mb-8">
        <h2 className="font-semibold text-dark mb-6">Order Tracking</h2>
        {order.status === 'problem' ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            There is an issue with this order. Please contact support for assistance.
          </div>
        ) : (
          <div className="flex items-center justify-between relative">
            {/* Progress line */}
            <div className="absolute left-0 right-0 top-5 h-0.5 bg-gray-200">
              <div
                className="h-full bg-gradient-to-r from-lime to-green transition-all duration-500"
                style={{ width: `${(currentStepIdx / (STEPS.length - 1)) * 100}%` }}
              />
            </div>

            {STEPS.map((step, i) => (
              <div key={step} className="relative flex flex-col items-center z-10">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    i <= currentStepIdx
                      ? 'bg-gradient-to-br from-lime to-green text-dark shadow-lg shadow-green/30'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {i < currentStepIdx ? '✓' : i + 1}
                </div>
                <span className={`mt-2 text-xs font-medium capitalize ${
                  i <= currentStepIdx ? 'text-dark' : 'text-gray-400'
                }`}>
                  {step}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Items */}
      <div className="glass rounded-2xl p-6 mb-8">
        <h2 className="font-semibold text-dark mb-4">Items</h2>
        <div className="divide-y divide-gray-100">
          {order.items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-dark text-sm">{item.productName}</p>
                <p className="text-xs text-gray-500">
                  {item.variantSize} {item.variantUnit} · {item.quantity} × {formatPrice(item.pricePerUnit)}
                </p>
              </div>
              <p className="font-semibold text-dark">{formatPrice(item.subtotal)}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-gray-200 pt-3 mt-3 flex justify-between">
          <span className="font-semibold text-dark">Total</span>
          <span className="text-xl font-bold text-dark">{formatPrice(order.total)}</span>
        </div>
      </div>

      {/* Status History */}
      <div className="glass rounded-2xl p-6">
        <h2 className="font-semibold text-dark mb-4">Status History</h2>
        <div className="space-y-3">
          {order.statusHistory.map((change, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-green mt-2 shrink-0" />
              <div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={change.status} />
                  <span className="text-xs text-gray-400">{formatDate(change.timestamp)}</span>
                </div>
                {change.note && (
                  <p className="text-sm text-gray-500 mt-1">{change.note}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {order.note && (
        <div className="glass rounded-2xl p-6 mt-8">
          <h2 className="font-semibold text-dark mb-2">Order Note</h2>
          <p className="text-sm text-gray-500">{order.note}</p>
        </div>
      )}
    </div>
  );
}
